// This file is a React component, not a workflow YAML. The workflow validation hook is a false positive.
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Brain, Network, Target, Truck, Activity, ChevronRight } from 'lucide-react';
import {
  listDashboardRuns,
  listWorkflowAuditEntries,
  cancelWorkflowRun,
  resumeWorkflowRun,
  abandonWorkflowRun,
  deleteWorkflowRun,
  approveWorkflowRun,
  rejectWorkflowRun,
  listCodebases,
  getHealth,
  type DashboardRunResponse,
} from '@/lib/api';
import type { WorkflowRunStatus } from '@/lib/types';
import { ensureUtc } from '@/lib/format';
import { getProjectDisplayName } from '@/lib/repo-label';
import { type WorkflowAuditDateRange } from '@/lib/workflow-audit';
import { cn } from '@/lib/utils';
import { WorkflowRunGroup } from '@/components/dashboard/WorkflowRunGroup';
import { WorkflowRunCard } from '@/components/dashboard/WorkflowRunCard';
import {
  DeployAssuranceView,
  type AssurancePage,
  type ComponentProgress,
  type StepStatus,
} from '@/components/deploy/DeployAssuranceModal';
import { WorkflowHistoryTable } from '@/components/dashboard/WorkflowHistoryTable';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { useWorkflowStore } from '@/stores/workflow-store';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

function getDateBounds(range: WorkflowAuditDateRange): { after?: string; before?: string } {
  if (range === 'all') return {};
  const now = new Date();
  const start = new Date(now);
  if (range === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (range === '7d') {
    start.setDate(start.getDate() - 7);
  } else if (range === '30d') {
    start.setDate(start.getDate() - 30);
  }
  return { after: start.toISOString() };
}

export function DashboardPage(): React.ReactElement {
  // Deploy assurance: "is what is RUNNING built from what we merged?" Progress is
  // operator-entered rather than fetched, because the freshness checker reads git
  // and kubectl and the cloud instance can do neither — a panel that rendered a
  // status it never measured would be the failure this subsystem exists to end.
  const [assuranceOpen, setAssuranceOpen] = useState(false);
  const [assurancePage, setAssurancePage] = useState<AssurancePage>('components');
  const [assuranceComponent, setAssuranceComponent] = useState<string | null>(null);
  const [assuranceProgress, setAssuranceProgress] = useState<Record<string, ComponentProgress>>({});

  function updateProgress(name: string, patch: Partial<ComponentProgress>): void {
    setAssuranceProgress(prev => {
      // Defaults come from a named value rather than literals ahead of the
      // spreads — TS2783 flags the literal-then-overwrite form, and it reads
      // ambiguously anyway.
      const existing: ComponentProgress = prev[name] ?? { status: 'pending', evidence: '' };
      return { ...prev, [name]: { ...existing, ...patch } };
    });
  }

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hydrateWorkflow = useWorkflowStore(state => state.hydrateWorkflow);

  // Hydrate filter state from URL (supports bookmarkable views)
  const statusFilter = searchParams.get('status') ?? null;
  const searchQuery = searchParams.get('q') ?? '';
  const projectFilter = searchParams.get('project') ?? null;
  const dateRange: WorkflowAuditDateRange =
    (searchParams.get('range') as WorkflowAuditDateRange) ?? 'all';
  const page = Math.max(0, Number(searchParams.get('page') ?? '0'));
  const pageSizeParam = Number(searchParams.get('pageSize') ?? '0');
  const pageSize = PAGE_SIZE_OPTIONS.includes(pageSizeParam as (typeof PAGE_SIZE_OPTIONS)[number])
    ? pageSizeParam
    : DEFAULT_PAGE_SIZE;

  // Debounced search: type instantly in the input, but delay the server request
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync searchInput when URL changes externally (e.g., back/forward)
  useEffect(() => {
    setSearchInput(searchParams.get('q') ?? '');
  }, [searchParams]);

  /** Helper to update URL params (replaces history entry to avoid back-spam). */
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === '' || v === '0' || v === 'all') {
              next.delete(k);
            } else {
              next.set(k, v);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatusFilter = useCallback(
    (status: string | null) => {
      updateParams({ status, page: null });
    },
    [updateParams]
  );
  const setProjectFilter = useCallback(
    (project: string | null) => {
      updateParams({ project, page: null });
    },
    [updateParams]
  );
  const setDateRange = useCallback(
    (range: WorkflowAuditDateRange) => {
      updateParams({ range, page: null });
    },
    [updateParams]
  );
  const setPage = useCallback(
    (p: number) => {
      updateParams({ page: p === 0 ? null : String(p) });
    },
    [updateParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      updateParams({
        pageSize: size === DEFAULT_PAGE_SIZE ? null : String(size),
        page: null,
      });
    },
    [updateParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateParams({ q: value || null, page: null });
      }, 300);
    },
    [updateParams]
  );

  // Compute date bounds from range preset
  const dateBounds = useMemo(() => getDateBounds(dateRange), [dateRange]);

  // Server-side fetch with all filters
  const {
    data: dashboardData,
    isLoading,
    isError,
    error: fetchError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: [
      'dashboardRuns',
      {
        status: statusFilter,
        codebaseId: projectFilter,
        search: searchQuery,
        dateRange,
        page,
        pageSize,
      },
    ],
    queryFn: () =>
      listDashboardRuns({
        status: (statusFilter as WorkflowRunStatus) ?? undefined,
        codebaseId: projectFilter ?? undefined,
        search: searchQuery || undefined,
        after: dateBounds.after,
        before: dateBounds.before,
        limit: pageSize,
        offset: page * pageSize,
      }),
    refetchInterval: 5_000,
  });

  const runs = dashboardData?.runs ?? [];
  const total = dashboardData?.total ?? 0;

  // Hydrate Zustand store from REST-polled data for active runs.
  // Only sets initial state if the run isn't already tracked by SSE.
  useEffect(() => {
    for (const run of runs) {
      if (run.status === 'running' || run.status === 'pending' || run.status === 'paused') {
        hydrateWorkflow({
          runId: run.id,
          workflowName: run.workflow_name,
          status: run.status,
          dagNodes: [],
          artifacts: [],
          startedAt: new Date(ensureUtc(run.started_at)).getTime(),
          currentTool: null,
        });
      }
    }
  }, [runs, hydrateWorkflow]);

  const { data: codebases } = useQuery({
    queryKey: ['codebases'],
    queryFn: () => listCodebases(),
  });

  // Fetch recent audit entries (last 5) for the right rail preview
  const { data: recentAuditData } = useQuery({
    queryKey: ['workflowAuditFeedRecent', { codebaseId: projectFilter }],
    queryFn: () =>
      listWorkflowAuditEntries({
        codebaseId: projectFilter ?? undefined,
        limit: 5,
      }),
    refetchInterval: 30_000,
  });

  const recentAuditEntries = recentAuditData?.entries ?? [];
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  // Split into active and history (from server-filtered results)
  const activeRuns = useMemo(
    () =>
      runs.filter(r => r.status === 'running' || r.status === 'pending' || r.status === 'paused'),
    [runs]
  );

  /**
   * Group active runs by parent_platform_id.
   * Multi-run groups (2+ runs from the same chat) get their own row with a header.
   * Singleton groups (1 run) are collected into a shared grid so they sit side-by-side.
   */
  const { multiRunGroups, singletonRuns } = useMemo(() => {
    const groups = new Map<
      string,
      { parentPlatformId: string | null; runs: DashboardRunResponse[] }
    >();
    for (const run of activeRuns) {
      const key = run.parent_platform_id ?? '__standalone__';
      const existing = groups.get(key);
      if (existing) {
        existing.runs.push(run);
      } else {
        groups.set(key, {
          parentPlatformId: run.parent_platform_id,
          runs: [run],
        });
      }
    }
    const multi: { parentPlatformId: string | null; runs: DashboardRunResponse[] }[] = [];
    const singles: DashboardRunResponse[] = [];
    for (const group of groups.values()) {
      if (group.runs.length > 1) {
        multi.push(group);
      } else {
        singles.push(group.runs[0]);
      }
    }
    return { multiRunGroups: multi, singletonRuns: singles };
  }, [activeRuns]);

  const historyRuns = useMemo(
    () =>
      runs.filter(
        r => r.status === 'completed' || r.status === 'failed' || r.status === 'cancelled'
      ),
    [runs]
  );

  const [actionError, setActionError] = useState<string | null>(null);

  async function runAction(
    action: (runId: string) => Promise<unknown>,
    runId: string,
    fallbackMessage: string
  ): Promise<void> {
    try {
      setActionError(null);
      await action(runId);
      void queryClient.invalidateQueries({ queryKey: ['dashboardRuns'] });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : fallbackMessage);
    }
  }

  const handleCancel = (runId: string): Promise<void> =>
    runAction(cancelWorkflowRun, runId, 'Failed to cancel workflow');
  const handleResume = (runId: string): Promise<void> =>
    runAction(resumeWorkflowRun, runId, 'Failed to resume workflow');
  const handleAbandon = (runId: string): Promise<void> =>
    runAction(abandonWorkflowRun, runId, 'Failed to abandon workflow');
  const handleDelete = (runId: string): Promise<void> =>
    runAction(deleteWorkflowRun, runId, 'Failed to delete workflow run');
  const handleApprove = (runId: string): Promise<void> =>
    runAction(approveWorkflowRun, runId, 'Failed to approve workflow');
  const handleReject = (runId: string): Promise<void> =>
    runAction(rejectWorkflowRun, runId, 'Failed to reject workflow');

  const totalPages = Math.ceil(total / pageSize);
  const hasMore = page + 1 < totalPages;

  // Placeholder to reference component functions (they're used below as JSX)
  void [
    ProjectPickerPanel,
    QuickStatsPanel,
    CapacityPanel,
    FiltersPanel,
    SystemHealthCard,
    QuickLaunchCard,
    RecentActivityCard,
  ];

  // Task 1: ProjectPickerPanel

  function ProjectPickerPanel(): React.ReactElement | null {
    return (
      <section className="rounded-2xl border border-border/70 bg-surface/90 p-4 shadow-sm">
        <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
          Project Scope
        </label>
        <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-surface-elevated">
          <button
            onClick={() => {
              setProjectFilter(null);
            }}
            className={cn(
              'w-full px-3 py-2 text-left text-xs text-text-secondary transition-colors border-b border-border/30 last:border-b-0',
              !projectFilter && 'bg-primary/10 text-primary font-medium'
            )}
          >
            All projects
          </button>
          {codebases?.map(codebase => (
            <button
              key={codebase.id}
              onClick={() => {
                setProjectFilter(codebase.id);
              }}
              className={cn(
                'w-full px-3 py-2 text-left text-xs text-text-secondary transition-colors border-b border-border/30 last:border-b-0',
                projectFilter === codebase.id && 'bg-primary/10 text-primary font-medium'
              )}
            >
              {getProjectDisplayName({
                name: codebase.name,
                repository_url: codebase.repository_url,
              })}
            </button>
          ))}
        </div>
      </section>
    );
  }

  // Task 2: QuickStatsPanel

  function QuickStatsPanel(): React.ReactElement {
    const statusCounts = dashboardData?.counts || { running: 0, pending: 0, paused: 0, failed: 0 };

    const stats = [
      {
        label: 'Running',
        value: statusCounts.running,
        status: 'running' as const,
        color: 'text-success',
      },
      {
        label: 'Pending',
        value: statusCounts.pending,
        status: 'pending' as const,
        color: 'text-warning',
      },
      {
        label: 'Paused',
        value: statusCounts.paused,
        status: 'paused' as const,
        color: 'text-text-secondary',
      },
      {
        label: 'Failed',
        value: statusCounts.failed,
        status: 'failed' as const,
        color: 'text-error',
      },
    ];

    return (
      <section className="space-y-3 rounded-2xl border border-border/70 bg-surface/90 p-4 shadow-sm">
        <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
          Quick Stats
        </label>
        {stats.map(stat => (
          <button
            key={stat.status}
            onClick={() => {
              setStatusFilter(stat.status);
            }}
            className={cn(
              'w-full px-3 py-2 rounded border text-sm font-medium transition-colors flex justify-between items-center',
              statusFilter === stat.status
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border bg-surface hover:bg-surface-elevated'
            )}
          >
            <span className={stat.color}>{stat.label}</span>
            <span className="font-semibold">{stat.value}</span>
          </button>
        ))}
      </section>
    );
  }

  // Task 3: CapacityPanel

  function CapacityPanel(): React.ReactElement {
    const active = health?.concurrency?.active ?? 0;
    const max = health?.concurrency?.maxConcurrent ?? 1;
    const percent = Math.min(100, Math.round((active / max) * 100));

    let barColor = 'bg-success';
    if (percent >= 90) barColor = 'bg-error';
    else if (percent >= 70) barColor = 'bg-warning';

    return (
      <section className="space-y-3 rounded-2xl border border-border/70 bg-surface/90 p-4 shadow-sm">
        <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
          Runtime Capacity
        </label>
        <div className="w-full overflow-hidden rounded-full bg-surface-elevated h-2">
          <div className={cn('h-full transition-all', barColor)} style={{ width: `${percent}%` }} />
        </div>
        <div className="text-xs text-text-secondary text-right">
          {active} / {max} active
        </div>
      </section>
    );
  }

  // Task 4: FiltersPanel

  function FiltersPanel(): React.ReactElement {
    const rangeOptions = [
      { label: 'Today', value: 'today' as const },
      { label: '7 days', value: '7d' as const },
      { label: '30 days', value: '30d' as const },
      { label: 'All', value: 'all' as const },
    ];

    return (
      <section className="space-y-3 rounded-2xl border border-border/70 bg-surface/90 p-4 shadow-sm">
        <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
          Date Range
        </label>
        {rangeOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => {
              setDateRange(opt.value === 'all' ? 'all' : opt.value);
            }}
            className={cn(
              'w-full px-3 py-2 rounded border text-sm font-medium transition-colors',
              dateRange === opt.value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border bg-surface hover:bg-surface-elevated'
            )}
          >
            {opt.label}
          </button>
        ))}
      </section>
    );
  }

  // Task 5: SystemHealthCard

  function SystemHealthCard(): React.ReactElement {
    const isHealthy = health?.status === 'ok';
    const statusColor = isHealthy ? 'text-success' : 'text-error';
    const statusLabel = isHealthy ? 'Healthy' : 'Degraded';

    return (
      <Card className="mx-4 rounded-2xl border-border/70 bg-surface/90 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={cn('w-2 h-2 rounded-full', statusColor.replace('text-', 'bg-'))} />
              <span className="text-sm font-medium">{statusLabel}</span>
            </div>
            {health?.version && (
              <span className="text-xs text-text-tertiary bg-surface px-2 py-1 rounded">
                v{health.version}
              </span>
            )}
          </div>
          <div className="text-xs text-text-secondary space-y-1">
            <div>Running: {dashboardData?.counts?.running ?? 0}</div>
            <div>
              Concurrency: {health?.concurrency?.active ?? 0} /{' '}
              {health?.concurrency?.maxConcurrent ?? 0}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setAssuranceOpen(true);
            }}
            className="w-full rounded-lg border border-border/70 px-3 py-2 text-xs text-text-secondary transition-colors hover:border-border hover:text-text-primary"
          >
            Deploy assurance
          </button>
        </CardContent>
      </Card>
    );
  }

  // Task 6: QuickLaunchCard

  interface QuickLaunchProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    route: string;
  }

  function QuickLaunchCard({
    icon,
    title,
    description,
    route,
  }: QuickLaunchProps): React.ReactElement {
    return (
      <Card
        className="mx-4 cursor-pointer rounded-2xl border-border/70 bg-surface/90 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-surface-elevated"
        onClick={() => navigate(route)}
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <div className="text-accent-bright">{icon}</div>
            <span className="font-medium text-sm">{title}</span>
          </div>
          <p className="text-xs text-text-secondary">{description}</p>
          <div className="flex items-center gap-1 text-xs text-accent-bright">
            Open <ChevronRight size={14} />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Task 7: RecentActivityCard

  function RecentActivityCard(): React.ReactElement {
    return (
      <Card className="mx-4 rounded-2xl border-border/70 bg-surface/90 shadow-sm">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-accent-bright" />
            <span className="font-medium text-sm">Recent Activity</span>
          </div>
          {recentAuditEntries && recentAuditEntries.length > 0 ? (
            <>
              {recentAuditEntries.slice(0, 5).map(entry => (
                <div
                  key={entry.id}
                  className="text-xs text-text-secondary border-t border-border pt-2"
                >
                  <div className="font-medium text-text-primary">{entry.event_type}</div>
                  <div>{entry.actor || 'system'}</div>
                  <div className="text-text-tertiary text-xs">
                    {new Date(entry.created_at).toLocaleTimeString()}
                  </div>
                </div>
              ))}
              <button
                onClick={() => navigate('/settings')}
                className="w-full mt-3 text-xs text-accent-bright hover:text-accent-bright/80 transition"
              >
                View all →
              </button>
            </>
          ) : (
            <div className="text-xs text-text-tertiary py-4 text-center">No recent activity</div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Mission Control" />
      <div className="flex flex-1 flex-row overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(96,165,250,0.08),_transparent_34%)]">
        {/* LEFT SIDEBAR — w-60 */}
        <aside className="w-60 border-r border-border/70 bg-surface/80 backdrop-blur-sm flex flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="space-y-4 p-4">
              <div className="rounded-2xl border border-border/60 bg-background/70 px-4 py-3 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                  Operations Rail
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Focus the command center by project, pressure, and time window.
                </div>
              </div>
              <ProjectPickerPanel />
              <QuickStatsPanel />
              <CapacityPanel />
              <FiltersPanel />
            </div>
          </ScrollArea>
        </aside>

        {/* CENTER — flex-1, scrollable */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-6 space-y-6">
            <section className="rounded-[28px] border border-border/70 bg-[linear-gradient(135deg,rgba(59,130,246,0.12),rgba(15,23,42,0.02)_42%,rgba(16,185,129,0.08))] p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                    Operational View
                  </p>
                  <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
                    Live Workflow Activity
                  </h1>
                  <p className="max-w-2xl text-sm leading-6 text-text-secondary">
                    Watch active orchestration, filter the run stream, and keep pressure signals in
                    view while operators respond.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                      Active
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-text-primary">
                      {activeRuns.length}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                      Last Refresh
                    </div>
                    <div className="mt-2 text-sm font-medium text-text-primary">
                      {dataUpdatedAt > 0
                        ? new Date(dataUpdatedAt).toLocaleTimeString()
                        : 'Awaiting data'}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="rounded-2xl border border-border/70 bg-surface/90 p-4 shadow-sm">
              <input
                type="text"
                placeholder="Search runs..."
                value={searchInput}
                onChange={(e): void => {
                  handleSearchChange(e.target.value);
                }}
                className="w-full rounded-xl border border-border bg-surface-elevated px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
              />
            </div>

            {actionError && (
              <div className="rounded-md border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
                {actionError}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-sm text-text-tertiary">Loading...</span>
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16">
                <p className="text-sm text-error">
                  Failed to load workflow runs
                  {fetchError instanceof Error ? `: ${fetchError.message}` : ''}
                </p>
              </div>
            ) : runs.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16">
                <p className="text-sm text-text-tertiary">No workflow runs found</p>
              </div>
            ) : (
              <>
                {/* Active Workflows */}
                {activeRuns.length > 0 && (
                  <section>
                    <h2 className="mb-3 text-sm font-semibold text-text-secondary">
                      Active Workflows
                    </h2>
                    <div className="space-y-6">
                      {singletonRuns.length > 0 && (
                        <div className="grid gap-4 md:grid-cols-2">
                          {singletonRuns.map(run => (
                            <WorkflowRunCard
                              key={run.id}
                              run={run}
                              isDocker={health?.is_docker}
                              onCancel={handleCancel}
                              onResume={handleResume}
                              onAbandon={handleAbandon}
                              onDelete={handleDelete}
                              onApprove={handleApprove}
                              onReject={handleReject}
                            />
                          ))}
                        </div>
                      )}
                      {multiRunGroups.map(group => (
                        <WorkflowRunGroup
                          key={group.parentPlatformId ?? 'standalone'}
                          parentPlatformId={group.parentPlatformId}
                          runs={group.runs}
                          isDocker={health?.is_docker}
                          onCancel={handleCancel}
                          onResume={handleResume}
                          onAbandon={handleAbandon}
                          onDelete={handleDelete}
                          onApprove={handleApprove}
                          onReject={handleReject}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* History */}
                {historyRuns.length > 0 && (
                  <section>
                    <h2 className="mb-3 text-sm font-semibold text-text-secondary">History</h2>
                    <WorkflowHistoryTable
                      runs={historyRuns}
                      onResume={handleResume}
                      onDelete={handleDelete}
                    />
                  </section>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-text-tertiary">
                      Showing {String(page * pageSize + 1)}&ndash;
                      {String(Math.min((page + 1) * pageSize, total))} of {String(total)} runs
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e): void => {
                        setPageSize(Number(e.target.value));
                      }}
                      className="rounded-md border border-border bg-surface-elevated px-2 py-1 text-xs text-text-primary focus:border-primary focus:outline-none"
                    >
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>
                          {String(size)} per page
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(): void => {
                        setPage(page - 1);
                      }}
                      disabled={page === 0}
                      className="rounded-md border border-border bg-surface-elevated px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-text-tertiary">
                      Page {String(page + 1)} of {String(Math.max(1, totalPages))}
                    </span>
                    <button
                      onClick={(): void => {
                        setPage(page + 1);
                      }}
                      disabled={!hasMore}
                      className="rounded-md border border-border bg-surface-elevated px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* RIGHT RAIL — w-72 */}
        <aside className="w-72 border-l border-border/70 bg-surface/80 backdrop-blur-sm flex flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="px-4 pt-4">
              <div className="rounded-2xl border border-border/60 bg-background/70 px-4 py-3 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                  Quick Launch
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Jump into the adjacent intelligence and delivery surfaces.
                </div>
              </div>
            </div>
            <SystemHealthCard />
            <Separator className="mx-4" />
            <QuickLaunchCard
              icon={<Brain size={18} />}
              title="G-Brain"
              description="Project pattern intelligence"
              route="/g-brain"
            />
            <QuickLaunchCard
              icon={<Network size={18} />}
              title="Intel"
              description="Cross-project synthesis"
              route="/intelligence"
            />
            <QuickLaunchCard
              icon={<Target size={18} />}
              title="Outcomes"
              description="ODD delivery tracking"
              route="/outcomes"
            />
            <QuickLaunchCard
              icon={<Truck size={18} />}
              title="Delivery"
              description="Project lifecycle overview"
              route="/delivery"
            />
            <Separator className="mx-4" />
            <RecentActivityCard />
          </ScrollArea>
        </aside>
      </div>

      <DeployAssuranceView
        open={assuranceOpen}
        page={assurancePage}
        selectedComponent={assuranceComponent}
        progress={assuranceProgress}
        onOpenChange={setAssuranceOpen}
        onPageChange={setAssurancePage}
        onSelectComponent={name => {
          setAssuranceComponent(name);
          setAssurancePage('diagnose');
        }}
        onStatusChange={(name, status: StepStatus) => {
          updateProgress(name, { status });
        }}
        onEvidenceChange={(name, evidence) => {
          updateProgress(name, { evidence });
        }}
        onFieldChange={(name, field, value) => {
          updateProgress(name, { [field]: value });
        }}
        onCopy={value => {
          void navigator.clipboard?.writeText(value);
        }}
      />
    </div>
  );
}
